import { createAppRequest } from '@/services/request';
import { unwrapApiResponse, type ApiRequestOptions } from './types/common';
import type {
  AnalyticsQuery,
  DashboardInvoiceQuery,
  FinanceAnalyticsDetailItem,
  FinanceAnalyticsDetailsQuery,
  FinanceAnalyticsQuery,
  FinanceAnalyticsResult,
  ManagementAnalyticsDetailsQuery,
  ManagementAnalyticsQuery,
  MyAnalyticsDetailItem,
  MyAnalyticsDetailsQuery,
  MyAnalyticsRankingItem,
  MyAnalyticsRankingQuery,
  MyAnalyticsQuery,
  ManagementAnalyticsSummaryResult,
  ManagementAnalyticsDetailItem,
  ApprovalTransferMember,
  BatchCreateInvoicePayload,
  BatchCreateInvoiceResult,
  BatchInvoiceOcrPayload,
  BatchInvoiceOcrResult,
  BuyerListQuery,
  BuyerRecord,
  ExpenseCategoryPayload,
  ExpenseCategoryRecord,
  ExpenseTypeDetailRecord,
  ExpenseTypeDetailPayload,
  AnomalyRuleListQuery,
  AnomalyHitListQuery,
  ApprovalTask,
  ApprovalTaskDetail,
  ApprovalTaskListQuery,
  BatchApprovalTaskResult,
  ApiResult,
  ApproveTaskPayload,
  CreateClaimFromImportArchivePayload,
  CreateFinancePayablePayload,
  DeleteExpenseClaimParams,
  DeleteExpenseClaimResult,
  ExpenseClaim,
  ExpenseClaimAmountSummary,
  ExpenseClaimDetail,
  ExpenseAnomalyRule,
  ExpenseAnomalyHit,
  ExpenseAnomalyRulePayload,
  ExpenseClaimPayload,
  ClaimItemPayload,
  FileBizType,
  DashboardAnalyticsResult,
  InvoiceDuplicateCheckPayload,
  InvoiceDuplicateCheckResult,
  InvoiceListQuery,
  InvoiceOcrPayload,
  InvoicePayload,
  InvoiceRecord,
  InvoiceStatusSummaryResult,
  ImportArchiveParseResult,
  UpdateInvoicePayload,
  TrendAnalyticsPoint,
  TransferTaskPayload,
  TenantAliyunOcrSettings,
  TenantPersonalizationSettings,
  TenantWorkflowSettings,
  UpdateTenantAliyunOcrSettingsPayload,
  DraftClaimCalculatePayload,
  DraftClaimCalculateResult,
  PersonalQuotaPreviewPayload,
  PersonalQuotaPreviewResult,
  UpdateTenantPersonalizationSettingsPayload,
  UpdateTenantWorkflowSettingsPayload,
  WorkflowTemplateOptionListResult,
  EfficiencyAnalyticsResult,
  AnomalyAnalyticsResult,
  OverviewAnalyticsResult,
  PaginatedResult,
  SignedUrlResult,
  InvoiceOcrParseResult,
  UploadFileResult,
  UserAnalyticsResult,
  UnassignedExpenseMonthClaim,
  ClaimListQuery,
  RejectTaskPayload,
  ExpenseClaimFinanceState,
  FinancePayableSyncView,
} from './types/reimbursement';
import { normalizeExpenseClaimFinanceState } from './financeState';
import { ensureValidAccessToken } from '@/services/oauth2';
import { readCurrentTenantUuid } from '@/adapters/tenantStorage';
import { BizAppConfig } from 'config/application.manifest';

const request = createAppRequest();

const normalizeOpenPlatformPath = (value: string) => value.replace(/^\/+|\/+$/g, '');

const getOrganizationMemberUrl = (pageNo: number, pageSize: number) => {
  const serviceUrl = BizAppConfig.appOpenServiceUrl?.trim().replace(/\/+$/, '');
  const gatewayPath = normalizeOpenPlatformPath(
    BizAppConfig.appOpenApiGatewayPath?.trim() || '',
  );
  const categoryCode = normalizeOpenPlatformPath(
    BizAppConfig.appOrganizationAdminCategoryCode?.trim() || '',
  );
  if (!serviceUrl || !gatewayPath || !categoryCode) {
    throw new Error('开放平台成员目录配置不完整');
  }
  return `${serviceUrl}/${gatewayPath}/${categoryCode}/api/v1/members?pageNo=${pageNo}&pageSize=${pageSize}`;
};

function unwrap<T>(response: ApiResult<T>): T {
  return unwrapApiResponse(response);
}

function unwrapNullableFinanceState(
  response: ApiResult<
    ExpenseClaimFinanceState | FinancePayableSyncView | null
  >,
): ExpenseClaimFinanceState | FinancePayableSyncView | null {
  if (response?.code !== 200) {
    throw new Error(response?.msg || '请求失败');
  }
  if (response.data === undefined) {
    throw new Error('服务返回数据为空');
  }
  return response.data;
}

export async function uploadFile(
  file: File,
  bizType: FileBizType,
  options?: ApiRequestOptions,
) {
  const formData = new FormData();
  formData.append('bizType', bizType);
  formData.append('file', file);

  const response = await request<ApiResult<UploadFileResult>>('/bff/files/upload', {
    method: 'POST',
    data: formData,
    ...(options || {}),
  });
  return unwrap(response);
}

export async function getFileDownloadUrl(
  objectKey: string,
  fileName?: string,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<SignedUrlResult>>('/bff/files/download-url', {
    method: 'GET',
    params: {
      objectKey,
      fileName,
    },
    ...(options || {}),
  });
  return unwrap(response);
}

export async function getFilePreviewUrl(
  objectKey: string,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<SignedUrlResult>>('/bff/files/preview-url', {
    method: 'GET',
    params: {
      objectKey,
    },
    ...(options || {}),
  });
  return unwrap(response);
}

export async function createInvoice(
  body: InvoicePayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<InvoiceRecord>>(
    '/bff/reimbursements/invoices',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function updateInvoice(
  uuid: string,
  body: UpdateInvoicePayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<InvoiceRecord>>(
    `/bff/reimbursements/invoices/${uuid}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function parseInvoiceByOcr(
  body: InvoiceOcrPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<InvoiceOcrParseResult>>(
    '/bff/reimbursements/invoices/ocr',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function parseInvoicesByBatchOcr(
  body: BatchInvoiceOcrPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<BatchInvoiceOcrResult>>(
    '/bff/reimbursements/invoices/ocr/batch',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function checkInvoiceDuplicates(
  body: InvoiceDuplicateCheckPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<InvoiceDuplicateCheckResult>>(
    '/bff/reimbursements/invoices/duplicate-check',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function parseImportArchive(
  file: File,
  options?: ApiRequestOptions,
) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await request<ApiResult<ImportArchiveParseResult>>(
    '/bff/reimbursements/import-archive/parse',
    {
      method: 'POST',
      data: formData,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function createClaimFromImportArchive(
  body: CreateClaimFromImportArchivePayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseClaimDetail>>(
    '/bff/reimbursements/import-archive/create-claim',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getTenantAliyunOcrSettings(options?: ApiRequestOptions) {
  const response = await request<ApiResult<TenantAliyunOcrSettings>>(
    '/bff/reimbursements/settings/aliyun-ocr',
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function updateTenantAliyunOcrSettings(
  body: UpdateTenantAliyunOcrSettingsPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<TenantAliyunOcrSettings>>(
    '/bff/reimbursements/settings/aliyun-ocr',
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getTenantPersonalizationSettings(
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<TenantPersonalizationSettings>>(
    '/bff/reimbursements/settings/personalization',
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function updateTenantPersonalizationSettings(
  body: UpdateTenantPersonalizationSettingsPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<TenantPersonalizationSettings>>(
    '/bff/reimbursements/settings/personalization',
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getTenantWorkflowSettings(options?: ApiRequestOptions) {
  const response = await request<ApiResult<TenantWorkflowSettings>>(
    '/bff/reimbursements/settings/workflow',
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function listInvoiceBuyers(
  params?: BuyerListQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PaginatedResult<BuyerRecord>>>(
    '/bff/reimbursements/settings/buyers',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function listAllInvoiceBuyers(
  params?: Omit<BuyerListQuery, 'page' | 'limit'>,
  options?: ApiRequestOptions,
) {
  const limit = 100;
  let page = 1;
  let total = 0;
  const items: BuyerRecord[] = [];

  do {
    const response = await listInvoiceBuyers(
      {
        ...params,
        page,
        limit,
      },
      options,
    );
    items.push(...(response.items || []));
    total = response.total || 0;
    if (!response.items?.length) {
      break;
    }
    page += 1;
  } while (items.length < total);

  return items;
}

export async function listExpenseCategories(options?: ApiRequestOptions) {
  const response = await request<ApiResult<ExpenseCategoryRecord[]>>(
    '/bff/reimbursements/expense-categories',
    { method: 'GET', ...(options || {}) },
  );
  return unwrap(response);
}

export async function listExpenseTypeDetails(params?: { categoryUuid?: string; keyword?: string }, options?: ApiRequestOptions) {
  const response = await request<ApiResult<ExpenseTypeDetailRecord[]>>('/bff/reimbursements/expense-types', { method: 'GET', params, ...(options || {}) });
  return unwrap(response);
}

export async function createExpenseTypeDetail(body: ExpenseTypeDetailPayload, options?: ApiRequestOptions) {
  const response = await request<ApiResult<ExpenseTypeDetailRecord>>('/bff/reimbursements/settings/expense-types', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, data: body, ...(options || {}),
  });
  return unwrap(response);
}

export async function updateExpenseTypeDetail(uuid: string, body: Omit<ExpenseTypeDetailPayload, 'code'>, options?: ApiRequestOptions) {
  const response = await request<ApiResult<ExpenseTypeDetailRecord>>(`/bff/reimbursements/settings/expense-types/${uuid}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, data: body, ...(options || {}),
  });
  return unwrap(response);
}

export async function setExpenseTypeDetailEnabled(uuid: string, enabled: boolean, options?: ApiRequestOptions) {
  const response = await request<ApiResult<ExpenseTypeDetailRecord>>(`/bff/reimbursements/settings/expense-types/${uuid}/${enabled ? 'enable' : 'disable'}`, {
    method: 'POST', ...(options || {}),
  });
  return unwrap(response);
}

export async function listAllExpenseCategories(options?: ApiRequestOptions) {
  const response = await request<ApiResult<ExpenseCategoryRecord[]>>(
    '/bff/reimbursements/settings/expense-categories',
    { method: 'GET', ...(options || {}) },
  );
  return unwrap(response);
}

export async function createExpenseCategory(
  body: ExpenseCategoryPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseCategoryRecord>>(
    '/bff/reimbursements/settings/expense-categories',
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, data: body, ...(options || {}) },
  );
  return unwrap(response);
}

export async function updateExpenseCategory(
  uuid: string,
  body: Omit<ExpenseCategoryPayload, 'code'>,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseCategoryRecord>>(
    `/bff/reimbursements/settings/expense-categories/${uuid}`,
    { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, data: body, ...(options || {}) },
  );
  return unwrap(response);
}

export async function setExpenseCategoryEnabled(
  uuid: string,
  enabled: boolean,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseCategoryRecord>>(
    `/bff/reimbursements/settings/expense-categories/${uuid}/${enabled ? 'enable' : 'disable'}`,
    { method: 'POST', ...(options || {}) },
  );
  return unwrap(response);
}

export async function calculateDraftClaimItems(payload: DraftClaimCalculatePayload) {
  const response = await request<ApiResult<DraftClaimCalculateResult>>(
    '/bff/reimbursements/claims/draft-calculate',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: payload,
    },
  );
  return unwrap(response);
}

export async function previewPersonalQuota(
  uuid: string,
  body: PersonalQuotaPreviewPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PersonalQuotaPreviewResult>>(
    `/bff/reimbursements/claims/${encodeURIComponent(uuid)}/quota-preview`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function listApprovalTodoTasks(
  params?: ApprovalTaskListQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PaginatedResult<ApprovalTask>>>(
    '/bff/reimbursements/approval-tasks/todo',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function listApprovalDoneTasks(
  params?: ApprovalTaskListQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PaginatedResult<ApprovalTask>>>(
    '/bff/reimbursements/approval-tasks/done',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getApprovalTaskDetail(taskId: string, options?: ApiRequestOptions) {
  const response = await request<ApiResult<ApprovalTaskDetail>>(
    `/bff/reimbursements/approval-tasks/${taskId}/detail`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export function getApprovalTaskInvoiceAttachmentsArchiveUrl(taskId: string) {
  const baseUrl = BizAppConfig.appApiUrl?.trim();
  const path = `/bff/reimbursements/approval-tasks/${encodeURIComponent(
    taskId,
  )}/invoice-attachments/archive`;

  if (!baseUrl) {
    return path;
  }

  return `${baseUrl.replace(/\/+$/, '')}${path}`;
}

export async function downloadApprovalTaskInvoiceAttachmentsArchive(taskId: string) {
  const accessToken = await ensureValidAccessToken();
  const tenantUuid = readCurrentTenantUuid();
  const url = getApprovalTaskInvoiceAttachmentsArchiveUrl(taskId);

  if (!accessToken || !tenantUuid) {
    throw new Error('当前会话缺少下载附件所需的认证信息');
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'x-tenant-id': tenantUuid,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `附件下载失败 (${response.status})`);
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition') || '';
  const encodedFileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  const plainFileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  const fileName = encodedFileNameMatch?.[1]
    ? decodeURIComponent(encodedFileNameMatch[1])
    : plainFileNameMatch?.[1] || '发票附件.zip';

  return {
    blob,
    fileName,
  };
}

export async function listApprovalTransferMembers(
  accessToken: string,
  memberUuid: string,
) {
  const pageSize = 100;
  let pageNo = 1;
  const members: ApprovalTransferMember[] = [];

  while (true) {
    const response = await fetch(getOrganizationMemberUrl(pageNo, pageSize), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
        memberUuid,
      },
    });
    if (!response.ok) {
      throw new Error(`成员目录加载失败 (${response.status})`);
    }

    const payload = (await response.json()) as {
      list?: Array<Record<string, unknown>>;
      total?: number;
    };
    const rawItems = payload.list || [];
    members.push(
      ...rawItems
        .map<ApprovalTransferMember>((item) => {
          const id =
            typeof item.memberUuid === 'string'
              ? item.memberUuid.trim()
              : typeof item.id === 'string'
                ? item.id.trim()
                : '';
          const name =
            typeof item.name === 'string'
              ? item.name.trim()
              : typeof item.nickname === 'string'
                ? item.nickname.trim()
                : typeof item.username === 'string'
                  ? item.username.trim()
                  : '';
          return {
            id,
            memberUuid: id,
            name,
            username:
              typeof item.username === 'string' ? item.username.trim() : undefined,
            phone: typeof item.phone === 'string' ? item.phone.trim() : undefined,
            email: typeof item.email === 'string' ? item.email.trim() : undefined,
            organizationUuid:
              typeof item.organizationUuid === 'string'
                ? item.organizationUuid.trim()
                : undefined,
          };
        })
        .filter((item) => Boolean(item.id && item.name)),
    );

    const total = Number(payload.total || 0);
    if (!rawItems.length || rawItems.length < pageSize || pageNo * pageSize >= total) break;
    pageNo += 1;
  }

  return members;
}

export async function approveApprovalTask(
  taskId: string,
  body: ApproveTaskPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ApprovalTaskDetail>>(
    `/bff/reimbursements/approval-tasks/${taskId}/approve`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function rejectApprovalTask(
  taskId: string,
  body: RejectTaskPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ApprovalTaskDetail>>(
    `/bff/reimbursements/approval-tasks/${taskId}/reject`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function batchApproveApprovalTasks(
  taskIds: string[],
  body: ApproveTaskPayload = {},
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<BatchApprovalTaskResult>>(
    '/bff/reimbursements/approval-tasks/batch-approve',
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, data: { taskIds, ...body }, ...(options || {}) },
  );
  return unwrap(response);
}

export async function batchRejectApprovalTasks(
  taskIds: string[],
  body: RejectTaskPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<BatchApprovalTaskResult>>(
    '/bff/reimbursements/approval-tasks/batch-reject',
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, data: { taskIds, ...body }, ...(options || {}) },
  );
  return unwrap(response);
}

export async function rejectApprovalTaskToInitiator(
  taskId: string,
  body: RejectTaskPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ApprovalTaskDetail>>(
    `/bff/reimbursements/approval-tasks/${taskId}/reject-initiator`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function transferApprovalTask(
  taskId: string,
  body: TransferTaskPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ApprovalTaskDetail>>(
    `/bff/reimbursements/approval-tasks/${taskId}/transfer`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getTenantWorkflowTemplateOptions(options?: ApiRequestOptions) {
  const response = await request<ApiResult<WorkflowTemplateOptionListResult>>(
    '/bff/reimbursements/settings/workflow/templates',
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function updateTenantWorkflowSettings(
  body: UpdateTenantWorkflowSettingsPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<TenantWorkflowSettings>>(
    '/bff/reimbursements/settings/workflow',
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function createInvoicesInBatch(
  body: BatchCreateInvoicePayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<BatchCreateInvoiceResult>>(
    '/bff/reimbursements/invoices/batch',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function listInvoices(
  params?: InvoiceListQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PaginatedResult<InvoiceRecord>>>(
    '/bff/reimbursements/invoices',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getInvoiceStatusSummary(
  params?: InvoiceListQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<InvoiceStatusSummaryResult>>(
    '/bff/reimbursements/invoices/summary',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getInvoice(
  uuid: string,
  options?: ApiRequestOptions & {
    params?: Pick<InvoiceListQuery, 'scope'>;
  },
) {
  const response = await request<ApiResult<InvoiceRecord>>(
    `/bff/reimbursements/invoices/${uuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function deleteInvoice(uuid: string, options?: ApiRequestOptions) {
  const response = await request<ApiResult<{ deleted: boolean; uuid: string }>>(
    `/bff/reimbursements/invoices/${uuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function createExpenseClaim(
  body: ExpenseClaimPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseClaimDetail>>(
    '/bff/reimbursements/claims',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function listExpenseClaims(
  params?: ClaimListQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PaginatedResult<ExpenseClaim>>>(
    '/bff/reimbursements/claims',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getExpenseClaimAmountSummary(
  params?: ClaimListQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseClaimAmountSummary>>(
    '/bff/reimbursements/claims/summary/amount-by-status',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getExpenseClaim(uuid: string, options?: ApiRequestOptions) {
  const response = await request<ApiResult<ExpenseClaimDetail>>(
    `/bff/reimbursements/claims/${uuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function deleteExpenseClaim(
  uuid: string,
  params?: DeleteExpenseClaimParams,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<DeleteExpenseClaimResult>>(
    `/bff/reimbursements/claims/${uuid}`,
    {
      method: 'DELETE',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function updateExpenseClaim(
  uuid: string,
  body: ExpenseClaimPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseClaimDetail>>(
    `/bff/reimbursements/claims/${uuid}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function submitExpenseClaim(uuid: string, options?: ApiRequestOptions) {
  const response = await request<ApiResult<ExpenseClaimDetail>>(
    `/bff/reimbursements/claims/${uuid}/submit`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function retryExpenseClaimWorkflow(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseClaimDetail>>(
    `/bff/reimbursements/claims/${uuid}/workflow-retry`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function withdrawExpenseClaim(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseClaimDetail>>(
    `/bff/reimbursements/claims/${uuid}/withdraw`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function syncExpenseClaimWorkflow(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseClaimDetail>>(
    `/bff/reimbursements/claims/${uuid}/workflow-sync`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getFinancePayableSync(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const response = await request<
    ApiResult<ExpenseClaimFinanceState | FinancePayableSyncView | null>
  >(`/bff/reimbursements/claims/${uuid}/finance-payable`, {
    method: 'GET',
    ...(options || {}),
  });
  return unwrapNullableFinanceState(response);
}

export async function getExpenseClaimFinanceState(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return normalizeExpenseClaimFinanceState(
    await getFinancePayableSync(uuid, options),
  );
}

export async function retryFinancePayableSync(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const response = await request<
    ApiResult<ExpenseClaimFinanceState | FinancePayableSyncView>
  >(`/bff/reimbursements/claims/${uuid}/finance-payable/retry`, {
    method: 'POST',
    ...(options || {}),
  });
  return unwrap(response);
}

export async function createExpenseClaimFinancePayable(
  uuid: string,
  body: CreateFinancePayablePayload,
  options?: ApiRequestOptions,
) {
  const response = await request<
    ApiResult<ExpenseClaimFinanceState | FinancePayableSyncView>
  >(`/bff/reimbursements/claims/${uuid}/finance-payable`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
  return normalizeExpenseClaimFinanceState(unwrap(response));
}

export async function reconcileFinancePayableSync(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const response = await request<
    ApiResult<ExpenseClaimFinanceState | FinancePayableSyncView | null>
  >(`/bff/reimbursements/claims/${uuid}/finance-payable/reconcile`, {
    method: 'POST',
    ...(options || {}),
  });
  return unwrapNullableFinanceState(response);
}

export async function syncExpenseClaimFinanceState(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const response = await request<
    ApiResult<ExpenseClaimFinanceState | FinancePayableSyncView | null>
  >(`/bff/reimbursements/claims/${uuid}/finance-state/sync`, {
    method: 'POST',
    ...(options || {}),
  });
  return normalizeExpenseClaimFinanceState(
    unwrapNullableFinanceState(response),
  );
}

export async function getMyReimbursementAnalytics(
  params?: MyAnalyticsQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<UserAnalyticsResult>>(
    '/bff/reimbursements/analytics/me',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getMyReimbursementAnalyticsDetails(
  params: MyAnalyticsDetailsQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PaginatedResult<MyAnalyticsDetailItem>>>(
    '/bff/reimbursements/analytics/me-details',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getMyReimbursementAnalyticsRankings(
  params: MyAnalyticsRankingQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PaginatedResult<MyAnalyticsRankingItem>>>(
    '/bff/reimbursements/analytics/me-rankings',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function listUnassignedExpenseMonthClaims(
  params?: { page?: number; limit?: number },
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PaginatedResult<UnassignedExpenseMonthClaim>>>(
    '/bff/reimbursements/analytics/unassigned-expense-month-claims',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getOverviewReimbursementAnalytics(
  params?: AnalyticsQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<OverviewAnalyticsResult>>(
    '/bff/reimbursements/analytics/overview',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getDashboardReimbursementAnalytics(
  params?: AnalyticsQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<DashboardAnalyticsResult>>(
    '/bff/reimbursements/analytics/dashboard',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getManagementAnalyticsSummary(
  params?: ManagementAnalyticsQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ManagementAnalyticsSummaryResult>>(
    '/bff/reimbursements/analytics/management-summary',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getManagementAnalyticsDetails(
  params: ManagementAnalyticsDetailsQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<
    ApiResult<PaginatedResult<ManagementAnalyticsDetailItem>>
  >('/bff/reimbursements/analytics/management-details', {
    method: 'GET',
    params,
    ...(options || {}),
  });
  return unwrap(response);
}

export async function getDashboardReimbursementAnalyticsInvoices(
  params?: DashboardInvoiceQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PaginatedResult<InvoiceRecord>>>(
    '/bff/reimbursements/analytics/dashboard-invoices',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getFinanceReimbursementAnalytics(
  params?: FinanceAnalyticsQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<FinanceAnalyticsResult>>(
    '/bff/reimbursements/analytics/finance',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getFinanceReimbursementAnalyticsDetails(
  params: FinanceAnalyticsDetailsQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<
    ApiResult<PaginatedResult<FinanceAnalyticsDetailItem>>
  >('/bff/reimbursements/analytics/finance-details', {
    method: 'GET',
    params,
    ...(options || {}),
  });
  return unwrap(response);
}

export async function getTrendReimbursementAnalytics(
  params?: AnalyticsQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<
    ApiResult<{
      granularity: AnalyticsQuery['granularity'];
      points: TrendAnalyticsPoint[];
    }>
  >('/bff/reimbursements/analytics/trend', {
    method: 'GET',
    params,
    ...(options || {}),
  });
  return unwrap(response);
}

export async function getEfficiencyReimbursementAnalytics(
  params?: AnalyticsQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<EfficiencyAnalyticsResult>>(
    '/bff/reimbursements/analytics/efficiency',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getAnomalyReimbursementAnalytics(
  params?: AnalyticsQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<AnomalyAnalyticsResult>>(
    '/bff/reimbursements/analytics/anomalies',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function listExpenseAnomalyRules(
  params?: AnomalyRuleListQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PaginatedResult<ExpenseAnomalyRule>>>(
    '/bff/reimbursements/anomaly-rules',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function listExpenseAnomalyHits(
  params?: AnomalyHitListQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PaginatedResult<ExpenseAnomalyHit>>>(
    '/bff/reimbursements/anomaly-hits',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function getExpenseAnomalyRule(uuid: string, options?: ApiRequestOptions) {
  const response = await request<ApiResult<ExpenseAnomalyRule>>(
    `/bff/reimbursements/anomaly-rules/${uuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function createExpenseAnomalyRule(
  body: ExpenseAnomalyRulePayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseAnomalyRule>>(
    '/bff/reimbursements/anomaly-rules',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function updateExpenseAnomalyRule(
  uuid: string,
  body: Partial<ExpenseAnomalyRulePayload>,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseAnomalyRule>>(
    `/bff/reimbursements/anomaly-rules/${uuid}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function deleteExpenseAnomalyRule(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<{ deleted: boolean; uuid: string }>>(
    `/bff/reimbursements/anomaly-rules/${uuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  return unwrap(response);
}
